import { esRutaCacheable, guardarCache, leerCache } from './offlineDb';
// ============================================================================
// Cliente de API centralizado.
//
// ANTES: cada uno de los 12 componentes tenía su propio
//   fetch(`http://${window.location.hostname}:3001/api/...`)
// sin chequear nunca response.ok. Esa es la causa de las pantallas en
// blanco: si el backend devolvía un error 500 con { error: "..." }, el
// componente igual hacía setData(esoQueNoEraLoEsperado) y al intentar
// leer una propiedad que no existía, React explotaba sin red de
// contención (no había ErrorBoundary en ningún lado tampoco).
//
// AHORA: un solo lugar concentra:
//   1. La URL base (configurable por variable de entorno para Fase 3 /
//      despliegue en la nube, en vez de tener "3001" hardcodeado 51 veces).
//   2. El token JWT (se agrega solo en cada request).
//   3. El chequeo de response.ok ANTES de devolver datos.
//   4. Un objeto de error consistente: { mensaje, status } que los
//      componentes pueden mostrar en pantalla en vez de solo loguear.
// ============================================================================

// Por defecto usa el mismo origen. En Docker, Nginx redirige /api al backend;
// en desarrollo, el campo proxy de package.json lo envía a localhost:3001.
// REACT_APP_API_URL solo se usa cuando el backend vive en otro dominio.
export const BASE_URL = process.env.REACT_APP_API_URL || '';

const TOKEN_KEY = 'kiosco_token';
const USER_KEY = 'kiosco_usuario';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setSesion(token, usuario) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(usuario));
}

export function getUsuarioGuardado() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function limpiarSesion() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

// Cualquier componente que reciba un 401 dispara este callback (lo registra
// App.js una sola vez al montar). Así, sin importar en qué pantalla esté el
// cajero quede la sesión vencida, se lo vuelve a mandar al login en vez de
// dejarlo viendo una pantalla con datos a medio cargar.
let onSesionExpirada = null;
export function setOnSesionExpirada(callback) {
  onSesionExpirada = callback;
}

// Error tipado para que los componentes puedan mostrar `err.mensaje`
// directamente en la UI en vez de "[object Object]" o un stack trace.
export class ApiError extends Error {
  constructor(mensaje, status, code = null, details = null) {
    super(mensaje);
    this.mensaje = mensaje;
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const usuario = getUsuarioGuardado();
  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  } catch (err) {
    if ((options.method || 'GET') === 'GET' && esRutaCacheable(path)) {
      const cached = await leerCache(path, usuario).catch(() => null);
      if (cached) {
        window.dispatchEvent(new CustomEvent('offline-cache-used', { detail: { path, actualizado_en: cached.actualizado_en } }));
        return cached.data;
      }
    }
    throw new ApiError('No se pudo conectar con el servidor. Revisá tu conexión.', 0, 'NETWORK_ERROR');
  }

  // Sesión vencida o token inválido: limpiamos y dejamos que App.js
  // redirija al login en vez de mostrar un error genérico.
  // Un 401 durante el login significa credenciales incorrectas, no una
  // sesión expirada. Solo limpiamos la sesión cuando el 401 ocurre en una
  // ruta protegida distinta de /api/login.
  if (res.status === 401 && !path.startsWith('/api/login')) {
    limpiarSesion();
    if (onSesionExpirada) onSesionExpirada();
    throw new ApiError('Tu sesión expiró. Iniciá sesión nuevamente.', 401);
  }

  // Respuestas sin contenido (ej. algunos 204) no tienen JSON para parsear
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    // ESTA es la línea que faltaba en los 12 componentes originales.
    throw new ApiError((data && data.error) || `Error del servidor (HTTP ${res.status})`, res.status, data?.code || null, data?.details || null);
  }

  if ((options.method || 'GET') === 'GET' && esRutaCacheable(path)) {
    guardarCache(path, data, usuario).catch(() => {});
  }
  return data;
}

export const api = {
  get: (path) => request(path, { method: 'GET' }),
  post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) }),
  put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path) => request(path, { method: 'DELETE' }),
  upload: (path, formData) => request(path, { method: 'POST', body: formData }),
  // Para descargas binarias (excel/pdf), que necesitan el blob crudo
  // en vez de JSON, pero igual con el token y el chequeo de errores.
  download: async (path) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new ApiError((data && data.error) || `Error del servidor (HTTP ${res.status})`, res.status, data?.code || null, data?.details || null);
    }
    return res.blob();
  },
};
